import { listTasksQuerySchema, taskIdSchema } from '@quizdb/common';
import { TaskListFilters } from './taskEndpoints';

/**
 * The filters' schema, without the cursor, which pages rather than filters (QZ-D8). Built from
 * `listTasksQuerySchema` directly: it carries no refinement, so `.omit` is safe on it.
 */
const filterSchema = listTasksQuerySchema.omit({ cursor: true });

const FILTER_KEYS = ['status', 'tag', 'taskType', 'difficulty', 'language', 'jobId'] as const;

/**
 * Reads the task list's filters from the URL query string, dropping any unknown or invalid
 * value field by field rather than failing the whole parse (QZ-D8). A missing `status` becomes
 * `draft`, the schema's default.
 */
export const readFilters = (searchParams: URLSearchParams): TaskListFilters => {
  const candidate: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value !== null && filterSchema.shape[key].safeParse(value).success) {
      candidate[key] = value;
    }
  }
  return filterSchema.parse(candidate);
};

/** The URL query string for `filters`, omitting "any" values and the default status (QZ-D8). */
export const writeFilters = (filters: TaskListFilters): URLSearchParams => {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value === undefined || (key === 'status' && value === 'draft')) {
      continue;
    }
    params.set(key, value);
  }
  return params;
};

/** The task list's path for `filters`, such as `/tasks?status=approved&jobId=…`. */
export const taskListPath = (filters: TaskListFilters): string => {
  const query = writeFilters(filters).toString();
  return query ? `/tasks?${query}` : '/tasks';
};

/** A status change just made, shown on the next page with a link back to the task. */
export interface TaskNotice {
  outcome: 'approved' | 'rejected';
  taskId: string;
  question: string;
}

/**
 * What travels in router state between the task list and the task page: the list's filters, so
 * the next draft comes from the same list, and the notice of the last status change.
 */
export interface TaskNavState {
  from?: TaskListFilters;
  notice?: TaskNotice;
}

const readNotice = (value: unknown): TaskNotice | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const { outcome, taskId, question } = value as Record<string, unknown>;
  return (outcome === 'approved' || outcome === 'rejected') &&
    taskIdSchema.safeParse(taskId).success &&
    typeof question === 'string'
    ? { outcome, taskId: taskId as string, question }
    : undefined;
};

/** Router state is whatever the history holds, so it is checked rather than trusted. */
export const readTaskNavState = (state: unknown): TaskNavState => {
  if (typeof state !== 'object' || state === null) {
    return {};
  }
  const { from, notice } = state as Record<string, unknown>;
  const filters = filterSchema.safeParse(from);
  const parsedNotice = readNotice(notice);
  return {
    ...(from !== undefined && filters.success ? { from: filters.data } : {}),
    ...(parsedNotice ? { notice: parsedNotice } : {}),
  };
};
